import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Logo } from '../components/Logo';
import { ProgressBar } from '../components/ProgressBar';
import { RangeStep, RANGE_OPTIONS, getMidpointValue } from '../components/RangeSelector';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { ForensicGradePopup, calculateForensicGrade } from '../components/ForensicGradePopup';
import { TrustBlock } from '../components/TrustBlock';
import { FAQSection } from '../components/FAQSection';
import { TrendingDown, AlertTriangle, ArrowRight, Sparkles, Home, CreditCard, Wallet, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage keys
const STORAGE_KEYS = {
  SESSION_ID: 'audit_session_id',
  CURRENT_STEP: 'audit_current_step',
  SELECTIONS: 'audit_selections',
  SHOW_RESULTS: 'audit_show_results',
  APP_VERSION: 'audit_app_version'
};

// Current app version - increment when major flow changes happen
const APP_VERSION = '3.0'; // 4-Tap Audit with Range Buttons

// Generate or retrieve session ID
const getOrCreateSessionId = () => {
  let sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!sessionId) {
    sessionId = 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
  }
  return sessionId;
};

// Check if app version changed and clear old state if needed
const checkAndMigrateStorage = () => {
  const storedVersion = localStorage.getItem(STORAGE_KEYS.APP_VERSION);
  if (storedVersion !== APP_VERSION) {
    // Clear old wizard state (but keep session ID for tracking continuity)
    localStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
    localStorage.removeItem(STORAGE_KEYS.SELECTIONS);
    localStorage.removeItem(STORAGE_KEYS.SHOW_RESULTS);
    // Also clear old keys from previous versions
    localStorage.removeItem('audit_goals');
    localStorage.removeItem('audit_values');
    localStorage.setItem(STORAGE_KEYS.APP_VERSION, APP_VERSION);
    console.log('App updated to version', APP_VERSION, '- wizard state reset');
  }
};

// Run migration on load
checkAndMigrateStorage();

// Detect in-app browser
const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return /FBAN|FBAV|Instagram|FB_IAB|FBIOS|FBSS/i.test(ua);
};

// Load saved state from localStorage
const loadSavedState = () => {
  try {
    return {
      currentStep: parseInt(localStorage.getItem(STORAGE_KEYS.CURRENT_STEP)) || 0,
      selections: JSON.parse(localStorage.getItem(STORAGE_KEYS.SELECTIONS)) || {
        homeValue: null,
        mortgageBalance: null,
        shadowDebt: null,
        householdIncome: null,
      },
      showResults: localStorage.getItem(STORAGE_KEYS.SHOW_RESULTS) === 'true'
    };
  } catch (e) {
    return {
      currentStep: 0,
      selections: { homeValue: null, mortgageBalance: null, shadowDebt: null, householdIncome: null },
      showResults: false
    };
  }
};

// Calculate Interest Leak using the formula
const calculateInterestLeak = (selections) => {
  const homeValue = getMidpointValue('homeValue', selections.homeValue);
  const mortgageBalance = getMidpointValue('mortgageBalance', selections.mortgageBalance);
  const shadowDebt = getMidpointValue('shadowDebt', selections.shadowDebt);
  
  if (!homeValue || !mortgageBalance) return 0;
  
  // Current rates
  const mortgageRate = 0.055; // Average current mortgage rate ~5.5%
  const shadowDebtRate = 0.21; // Credit card / high-interest rate 21%
  const consolidationRate = 0.045; // 2026 restructured rate ~4.5%
  
  // Calculate current annual interest cost
  const currentMortgageInterest = mortgageBalance * mortgageRate;
  const currentShadowInterest = shadowDebt * shadowDebtRate;
  const currentTotalInterest = currentMortgageInterest + currentShadowInterest;
  
  // Calculate interest if consolidated at better rate
  const totalDebt = mortgageBalance + shadowDebt;
  const consolidatedInterest = totalDebt * consolidationRate;
  
  // Interest Leak = difference between current and optimized
  const interestLeak = Math.max(0, currentTotalInterest - consolidatedInterest);
  
  return Math.round(interestLeak);
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function HomePage() {
  const navigate = useNavigate();
  const savedState = loadSavedState();
  
  const [currentStep, setCurrentStep] = useState(savedState.currentStep);
  const [selections, setSelections] = useState(savedState.selections);
  const [showResults, setShowResults] = useState(savedState.showResults);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeakAnimation, setShowLeakAnimation] = useState(false);
  
  const sessionIdRef = useRef(getOrCreateSessionId());
  const trackedStepsRef = useRef(new Set());

  // Get midpoint values from selections
  const getMidpointValues = () => ({
    homeValue: getMidpointValue('homeValue', selections.homeValue),
    mortgageBalance: getMidpointValue('mortgageBalance', selections.mortgageBalance),
    shadowDebt: getMidpointValue('shadowDebt', selections.shadowDebt),
    householdIncome: getMidpointValue('householdIncome', selections.householdIncome),
  });

  // Calculate interest leak
  const interestLeak = calculateInterestLeak(selections);

  // Progress: Step 0=0%, Step 1=20%, Step 2=40%, Step 3=60%, Step 4=80%, Results=100%
  const getProgress = () => {
    if (showResults) return 100;
    if (currentStep === 0) return 0;
    return currentStep * 20;
  };

  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CURRENT_STEP, currentStep.toString());
    localStorage.setItem(STORAGE_KEYS.SELECTIONS, JSON.stringify(selections));
    localStorage.setItem(STORAGE_KEYS.SHOW_RESULTS, showResults.toString());
  }, [currentStep, selections, showResults]);

  // Track progress for analytics
  const trackProgress = useCallback((step, stepName, value) => {
    const stepKey = `${step}-${stepName}`;
    if (trackedStepsRef.current.has(stepKey)) return;

    const trackingData = {
      session_id: sessionIdRef.current,
      step: step,
      step_name: stepName,
      value: value,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      is_in_app: isInAppBrowser(),
    };

    // Try sendBeacon first (most reliable for page unload)
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(trackingData)], { type: 'application/json' });
      const success = navigator.sendBeacon(`${API}/track-progress`, blob);
      if (success) {
        trackedStepsRef.current.add(stepKey);
        return;
      }
    }

    // Fallback to fetch with keepalive
    fetch(`${API}/track-progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(trackingData),
      keepalive: true,
    }).then(() => {
      trackedStepsRef.current.add(stepKey);
    }).catch(() => {
      // Final fallback: pixel tracking
      const params = new URLSearchParams({
        session_id: sessionIdRef.current,
        step: step.toString(),
        step_name: stepName,
        value: value.toString(),
      });
      new Image().src = `${API}/track-pixel?${params}`;
      trackedStepsRef.current.add(stepKey);
    });
  }, []);

  // Handle range selection with auto-advance
  const handleRangeSelect = (field, rangeId) => {
    // Update selection
    setSelections(prev => ({ ...prev, [field]: rangeId }));
    
    // Track the step with midpoint value (backend expects a number)
    const stepNames = {
      homeValue: 'Home Value',
      mortgageBalance: 'Mortgage Balance',
      shadowDebt: 'Shadow Debt',
      householdIncome: 'Household Income',
    };
    const midpointValue = getMidpointValue(field, rangeId);
    trackProgress(currentStep, stepNames[field], midpointValue);
    
    // Auto-advance to next step after brief delay for visual feedback
    setTimeout(() => {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        // Show results
        setShowResults(true);
        setShowLeakAnimation(true);
      }
    }, 300);
  };

  const handleStartAudit = () => {
    setCurrentStep(1);
    trackProgress(0, 'Started Audit', 1);
  };

  const handleBack = () => {
    if (showResults) {
      setShowResults(false);
      setShowLeakAnimation(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const midpointValues = getMidpointValues();
      const gradeInfo = calculateForensicGrade(midpointValues);
      
      const payload = {
        first_name: '',
        last_name: '',
        email: formData.email,
        phone: '',
        home_value: midpointValues.homeValue,
        mortgage_balance: midpointValues.mortgageBalance,
        high_interest_debt: midpointValues.shadowDebt,
        monthly_cash_surplus: 0,
        interest_leak: interestLeak,
        goals: `Home: ${selections.homeValue}, Mortgage: ${selections.mortgageBalance}, Shadow Debt: ${selections.shadowDebt}, Income: ${selections.householdIncome}`,
        liquidity_grade: gradeInfo.grade,
        household_income: midpointValues.householdIncome,
      };

      const response = await axios.post(`${API}/leads`, payload);
      sessionStorage.setItem('leadData', JSON.stringify(response.data));
      
      // Clear localStorage on successful completion
      Object.values(STORAGE_KEYS).forEach(key => {
        if (key !== STORAGE_KEYS.APP_VERSION && key !== STORAGE_KEYS.SESSION_ID) {
          localStorage.removeItem(key);
        }
      });
      
      navigate('/results');
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOver = () => {
    Object.values(STORAGE_KEYS).forEach(key => {
      if (key !== STORAGE_KEYS.APP_VERSION) {
        localStorage.removeItem(key);
      }
    });
    trackedStepsRef.current.clear();
    sessionIdRef.current = getOrCreateSessionId();
    setCurrentStep(0);
    setSelections({ homeValue: null, mortgageBalance: null, shadowDebt: null, householdIncome: null });
    setShowResults(false);
    setShowLeakAnimation(false);
  };

  return (
    <div data-testid="home-page" className="min-h-screen bg-warm-50">
      {/* Progress Bar - Only show after Welcome screen */}
      {currentStep > 0 && <ProgressBar progress={getProgress()} />}
      
      {/* Header */}
      <header className="relative z-10 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4 md:py-5">
          <Logo size="small" showSubtitle={currentStep > 0} />
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-2xl mx-auto px-4 py-6 md:py-10">
        
        {/* Wizard Steps */}
        {!showResults && (
          <>
            {/* Step 0: Welcome Card */}
            {currentStep === 0 && (
              <div className="animate-fadeIn" data-testid="wizard-step-0">
                {/* Hero Welcome Card */}
                <div className="welcome-card rounded-2xl p-8 md:p-12 text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-400/20 mb-6">
                    <Sparkles className="w-8 h-8 text-gold-400" />
                  </div>
                  
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4 leading-tight">
                    Is your cash flow trapped?
                  </h1>
                  
                  <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-lg mx-auto leading-relaxed">
                    Most homeowners lose <span className="text-gold-400 font-semibold">$500+ every month</span> to high interest while their home equity sits idle. See your numbers in 60 seconds.
                  </p>
                  
                  <button
                    onClick={handleStartAudit}
                    data-testid="start-audit-btn"
                    className="inline-flex items-center gap-3 bg-gold-400 text-navy-950 hover:bg-gold-500 font-heading font-semibold text-lg px-10 py-5 rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    Start My Audit
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-2xl font-heading font-bold text-navy-950">4 Taps</div>
                    <div className="text-sm text-slate-500">to complete</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-2xl font-heading font-bold text-navy-950">Free</div>
                    <div className="text-sm text-slate-500">no cost ever</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-2xl font-heading font-bold text-navy-950">Private</div>
                    <div className="text-sm text-slate-500">no credit check</div>
                  </div>
                </div>

                {/* Location text */}
                <p className="text-center text-sm text-slate-500 mt-6 px-4">
                  Serving homeowners and self-employed borrowers across Ontario — Toronto, Mississauga, Brampton, Vaughan and the GTA.
                </p>
              </div>
            )}

            {/* Step 1: Home Value */}
            {currentStep === 1 && (
              <div className="animate-fadeIn" data-testid="wizard-step-1">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold-400/10 mb-4">
                    <Home className="w-7 h-7 text-gold-500" />
                  </div>
                  <p className="text-sm font-heading uppercase tracking-widest text-slate-500 mb-3">
                    Step 1 of 4
                  </p>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-navy-950 mb-3">
                    Estimated Home Value?
                  </h2>
                  <p className="text-slate-600">
                    What do you think your home is worth today?
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-lg mx-auto">
                  {RANGE_OPTIONS.homeValue.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleRangeSelect('homeValue', option.id)}
                      data-testid={`home-${option.id}`}
                      className={`
                        relative p-5 md:p-6 rounded-xl border-2 transition-all duration-200 ease-out
                        font-heading font-semibold text-base md:text-lg
                        ${selections.homeValue === option.id
                          ? 'bg-gold-400 border-gold-400 text-navy-950 shadow-lg scale-[1.02]'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-gold-400/50 hover:shadow-md'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Back button */}
                <div className="mt-8 text-center">
                  <button
                    onClick={handleBack}
                    className="text-slate-500 hover:text-slate-700 text-sm font-medium"
                  >
                    ← Back to start
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Mortgage Balance */}
            {currentStep === 2 && (
              <div className="animate-fadeIn" data-testid="wizard-step-2">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold-400/10 mb-4">
                    <Wallet className="w-7 h-7 text-gold-500" />
                  </div>
                  <p className="text-sm font-heading uppercase tracking-widest text-slate-500 mb-3">
                    Step 2 of 4
                  </p>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-navy-950 mb-3">
                    Total Mortgage Balance?
                  </h2>
                  <p className="text-slate-600">
                    The amount you still owe on your home.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-lg mx-auto">
                  {RANGE_OPTIONS.mortgageBalance.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleRangeSelect('mortgageBalance', option.id)}
                      data-testid={`mortgage-${option.id}`}
                      className={`
                        relative p-5 md:p-6 rounded-xl border-2 transition-all duration-200 ease-out
                        font-heading font-semibold text-base md:text-lg
                        ${selections.mortgageBalance === option.id
                          ? 'bg-gold-400 border-gold-400 text-navy-950 shadow-lg scale-[1.02]'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-gold-400/50 hover:shadow-md'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Back button */}
                <div className="mt-8 text-center">
                  <button
                    onClick={handleBack}
                    className="text-slate-500 hover:text-slate-700 text-sm font-medium"
                  >
                    ← Back
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Shadow Debt */}
            {currentStep === 3 && (
              <div className="animate-fadeIn" data-testid="wizard-step-3">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold-400/10 mb-4">
                    <CreditCard className="w-7 h-7 text-gold-500" />
                  </div>
                  <p className="text-sm font-heading uppercase tracking-widest text-slate-500 mb-3">
                    Step 3 of 4
                  </p>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-navy-950 mb-3">
                    Total "Shadow Debt"?
                  </h2>
                  <p className="text-slate-600">
                    Credit cards, HELOC, car loans, personal loans.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-lg mx-auto">
                  {RANGE_OPTIONS.shadowDebt.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleRangeSelect('shadowDebt', option.id)}
                      data-testid={`shadow-${option.id}`}
                      className={`
                        relative p-5 md:p-6 rounded-xl border-2 transition-all duration-200 ease-out
                        font-heading font-semibold text-base md:text-lg
                        ${selections.shadowDebt === option.id
                          ? 'bg-gold-400 border-gold-400 text-navy-950 shadow-lg scale-[1.02]'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-gold-400/50 hover:shadow-md'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Back button */}
                <div className="mt-8 text-center">
                  <button
                    onClick={handleBack}
                    className="text-slate-500 hover:text-slate-700 text-sm font-medium"
                  >
                    ← Back
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Household Income */}
            {currentStep === 4 && (
              <div className="animate-fadeIn" data-testid="wizard-step-4">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold-400/10 mb-4">
                    <DollarSign className="w-7 h-7 text-gold-500" />
                  </div>
                  <p className="text-sm font-heading uppercase tracking-widest text-slate-500 mb-3">
                    Step 4 of 4
                  </p>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-navy-950 mb-3">
                    Combined Household Income?
                  </h2>
                  <p className="text-slate-600">
                    Your total annual pre-tax income.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-lg mx-auto">
                  {RANGE_OPTIONS.householdIncome.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleRangeSelect('householdIncome', option.id)}
                      data-testid={`income-${option.id}`}
                      className={`
                        relative p-5 md:p-6 rounded-xl border-2 transition-all duration-200 ease-out
                        font-heading font-semibold text-base md:text-lg
                        ${selections.householdIncome === option.id
                          ? 'bg-gold-400 border-gold-400 text-navy-950 shadow-lg scale-[1.02]'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-gold-400/50 hover:shadow-md'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Back button */}
                <div className="mt-8 text-center">
                  <button
                    onClick={handleBack}
                    className="text-slate-500 hover:text-slate-700 text-sm font-medium"
                  >
                    ← Back
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Results */}
        {showResults && (
          <div className="animate-fadeIn" data-testid="results-reveal">
            {/* Back link */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Adjust numbers
            </button>

            {/* Interest Leak Reveal */}
            <div className="bg-white rounded-2xl p-8 md:p-10 text-center mb-8 shadow-lg border border-slate-200">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <TrendingDown className="w-8 h-8 text-red-500" />
              </div>

              <p className="text-lg text-slate-600 mb-2">Your Annual Interest Leak</p>
              
              <div className="text-5xl md:text-6xl font-heading font-bold text-red-500 mb-4" data-testid="leak-result-value">
                {showLeakAnimation ? (
                  <AnimatedCounter 
                    targetValue={interestLeak} 
                    duration={2000}
                    prefix="$"
                  />
                ) : (
                  formatCurrency(interestLeak)
                )}
              </div>
              
              <p className="text-slate-600 mb-6">
                per year leaking to inefficient interest payments
              </p>

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-red-600">
                  That's {formatCurrency(Math.round(interestLeak / 12))}/month you could recover
                </span>
              </div>
            </div>

            {/* Forensic Grade Popup */}
            <ForensicGradePopup
              values={getMidpointValues()}
              interestLeak={interestLeak}
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
            />

            {/* Start Over */}
            <div className="text-center mt-8">
              <button
                onClick={handleStartOver}
                className="text-sm text-slate-500 hover:text-slate-600 underline"
              >
                Start over with new numbers
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Trust Block */}
      <TrustBlock />

      {/* FAQ Section */}
      <FAQSection />

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Sanam Bawa | <a href="mailto:sanam@everythingmortgages.ca" className="hover:text-gold-500 transition-colors">sanam@everythingmortgages.ca</a> | License #M15001292
          </p>
          <p className="text-xs text-slate-400 mt-2">
            This assessment is for educational purposes only.
          </p>
        </div>
      </footer>
    </div>
  );
}
